import { PhotoIcon, UserCircleIcon } from '@heroicons/react/24/solid';
import { Account } from '@radixdlt/radix-dapp-toolkit';
import { ActionFunctionArgs, LoaderFunctionArgs, json } from '@remix-run/cloudflare';
import { Form, Link, useFetcher, useLoaderData, useNavigate } from '@remix-run/react';
import BigNumber from 'bignumber.js';
import { FormEvent, useEffect, useState } from 'react';
import AccountSelector from '~/components/account-selector';
import TokenInput from '~/components/token-input';
import TokenRow from '~/components/token-row';
import TransactionButton from '~/components/transaction-button';
import { fetchPrices } from '~/lib/astrolescent';
import { generateImageUrl } from '~/lib/cloudflare';
import { generateCreatePairManifest } from '~/lib/manifests';
import { createTokenManifest } from '~/lib/manifests/token';
import { getToken } from '~/lib/models/tokens';
import { DEFAULT_PAIR_FEE, MINIMUM_DFP2_AMOUNT, QUOTE_TOKEN_RESOURCE_ADDRESS, QUOTE_TOKEN_SYMBOL, XRD_RESOURCE_ADDRESS, classNames, onIconError, prettyValue, roundToDecimals } from '~/lib/util';
import { useGatewayApi } from '~/rdt/gateway';
import { useRadix } from '~/rdt/radixProvider';
import { loader as walletLoader } from '~/routes/wallet.$account';
import { loader as pairsLoader } from '~/routes/cronjob.new-pairs';
import { setDbFromContext } from '~/lib/db';
import { trackEvent } from '~/lib/analytics';

type TokenType = 'project' | 'meme' | 'custom';

const DEFAULT_MC_USD = 10000;

export async function loader({ params, context }: LoaderFunctionArgs) {
   setDbFromContext(context);

   const [quoteToken] = await getToken(QUOTE_TOKEN_RESOURCE_ADDRESS);
   const prices = await fetchPrices();

   // fetch Cloudflare upload url
   const url = `https://api.cloudflare.com/client/v4/accounts/${context.cloudflare.env.CLOUDFLARE_ACCOUNT_ID}/images/v2/direct_upload`;
   const formData = new FormData();
   formData.append('metadata', JSON.stringify({ type: 'token' }));
   formData.append('requireSignedURLs', 'false');
   const response = await fetch(url, {
      method: 'POST',
      headers: {
         Authorization: `Bearer ${context.cloudflare.env.CLOUDFLARE_API_TOKEN}`,
      },
      body: formData,
   });
   const result = await response.json();

   return json({
      uploadUrl: result.result.uploadURL,
      quoteToken,
      xrdPrice: prices[XRD_RESOURCE_ADDRESS]?.tokenPriceUSD || 0.04,
      quotePrice: prices[QUOTE_TOKEN_RESOURCE_ADDRESS]?.tokenPriceUSD || 0.035,
   });
}

export default function Launch() {
   const { uploadUrl, quoteToken, xrdPrice, quotePrice } = useLoaderData<typeof loader>();

   const { sendTransaction } = useRadix();
   const walletFetcher = useFetcher<typeof walletLoader>();
	const pairsFetcher = useFetcher<typeof pairsLoader>();
	const navigate = useNavigate();

   const [account, setAccount] = useState<Account | null>(null);

   const [tokenType, setTokenType] = useState<TokenType>('project');
   const [symbol, setSymbol] = useState<string>('');
   const [name, setName] = useState<string>('');
   const [description, setDescription] = useState<string>('');
   const [url, setUrl] = useState<string>('');
   const [supply, setSupply] = useState<number | null>();
   const [price, setPrice] = useState<number | null>();
   const [priceXRD, setPriceXRD] = useState<number | null>();
   const [fee, setFee] = useState(DEFAULT_PAIR_FEE);
   const [baseTokensRequired, setBaseTokensRequired] = useState<number>(0);
   const [tokenResourceAddress, setTokenResourceAddress] = useState<string | null>();

   const [imagePreview, setImagePreview] = useState<string | null>();
   const [imageURL, setImageURL] = useState<string | null>();
   const [imageFile, setImageFile] = useState<string | null>();

   const [done, setDone] = useState<boolean>(false);

   function onAccount(account: Account) {
      setAccount(account);

      walletFetcher.load(`/wallet/${account.address}`);
   }

   useEffect(() => {
      let newSupply = supply;
      if (tokenType === 'project') {
         newSupply = 100000000; // 100M
         setFee(0.004);
      } else if (tokenType === 'meme') {
         newSupply = 100000000000; // 100B
         setFee(0.01);
      }

      const newPriceXRD = newSupply ? new BigNumber(DEFAULT_MC_USD).div(newSupply).div(xrdPrice).toNumber() : null;

      setSupply(newSupply);
      onSetPriceXRD(newPriceXRD);
   }, [tokenType]);

   function onSetPriceXRD(p: string) {
      console.log(p);
      const newPriceInXRD = parseFloat(p);

      const newPrice = newPriceInXRD ? newPriceInXRD * xrdPrice : null;
      const tokensRequired = newPriceInXRD ? (MINIMUM_DFP2_AMOUNT * (xrdPrice / quotePrice)) / newPriceInXRD : 0;

      setPriceXRD(newPriceInXRD);
      setPrice(newPrice);
      setBaseTokensRequired(tokensRequired);
   }

   function onTypeChange(evt) {
      setTokenType(evt.target.value);
   }

   function onImageChange(event) {
      console.log('onImageChange');
      if (event.target.files && event.target.files[0]) {
         const file = event.target.files[0];

         // Use FileReader to read the file and display it
         const reader = new FileReader();
         reader.onload = (e) => {
            // Set the loaded file's data URL as the imagePreview state
            // console.log(e.target?.result)
            setImagePreview(e.target.result);
            setImageFile(file);
         };
         reader.readAsDataURL(file);
         setImageURL(null);
      }
   }

   async function onCreateToken(event: FormEvent<HTMLFormElement>) {
      event.preventDefault();

      if (!account) {
         return;
      }

      if (!uploadUrl) {
         console.log('No upload URL');
         return; // Exit if there's no upload URL
		}
		
		let tempImageURL = imageURL;

      if (!tempImageURL) {
         const formData = new FormData();
         if (imageFile) {
            formData.append('file', imageFile);
         } else {
            console.log('No file selected');
            return; // Exit the function if no file is selected
         }
         console.log('Preparing to upload file...');
         try {
            const response = await fetch(uploadUrl, {
               method: 'POST',
               body: formData, // Send the form data as the request body
            });

            if (!response.ok) {
               throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Upload successful, image public url:', data.result.variants[0]);
				const iconUrl = generateImageUrl(data.result.id);
				
				tempImageURL = iconUrl;
            setImageURL(iconUrl);
         } catch (error) {
            console.error('Upload failed:', error);
            return;
         }
      }

      // calculate priceXRD in DFP2
      // generate transasction manifest
      const transactionManifest = createTokenManifest(account.address, name, symbol, description, url, tempImageURL, supply);

      const result = await sendTransaction(transactionManifest, `Create ${name} (${symbol}) token on DefiPlaza`);

      trackEvent("send_tx", {
         type: "createToken",
      });

      // fetch staked token resource address and staking component address
      const txId = result.value.transactionIntentHash;

      const { transaction } = useGatewayApi;
      const status = await transaction.getCommittedDetails(txId);

      const tokenAddress = status.transaction.receipt?.state_updates?.created_substates.find((s) => s.substate_id.entity_type == 'GlobalFungibleResource')?.substate_id.entity_address;

      console.log('tokenAddress', tokenAddress);
      setTokenResourceAddress(tokenAddress);
   }

	async function onCreatePool(event: FormEvent<HTMLFormElement>) {
      event.preventDefault();

      if (!account || !tokenResourceAddress) {
         return;
      }

      // calculate priceXRD in DFP2
		const p0 = new BigNumber(priceXRD / (quotePrice / xrdPrice)).toFixed(18);
		const tokensRequired = new BigNumber(baseTokensRequired).toFixed(18);

      // generate transasction manifest
      const transactionManifest = generateCreatePairManifest(account.address, tokenResourceAddress, tokensRequired, p0, fee);

      const result = await sendTransaction(transactionManifest, `Create ${symbol}/${QUOTE_TOKEN_SYMBOL} pool on DefiPlaza`);

      trackEvent("send_tx", {
         type: "createPool",
      });

      // fetch staked token resource address and staking component address
		const txId = result.value.transactionIntentHash;
		
		pairsFetcher.load(`/cronjob/new-pairs`);

      setDone(true);
   }

   return (
      <Form method="post" className="container  mx-4 mb-8 lg:mx-auto">
         <h1 className="text-4xl font-bold text-left p-8 pb-2 text-white">Create new token</h1>
         <p className="p-8 pt-0">If you already have a token, you can <Link to="/liquidity/create" className="underline">create a liquidity pool</Link> to add it to DefiPlaza.</p>

         {done ? (
            <div className="space-y-8">
               <section className="flex flex-col space-y-4 bg-white rounded-xl lg:rounded-2xl p-4 lg:p-8 text-black">
                  <p className="font-bold">Congratulations! You launched {symbol}! 🚀</p>
                  <p>You can now add more liquidity to the pool on DefiPlaza to make it easier for traders to buy and sell {symbol}.</p>
                  <p>
                     Adding liquidity is single-sided so you don't need an equal amount in DFP2. Some DFP2 is needed however for traders to sell {symbol}, so it makes sense to add around $500 in DFP2
                     as well.
                  </p>
                  <p>
                     If you have any questions, visit us on{' '}
                     <a href="https://t.me/telegram" target="_blank" className="underline">
                        Telegram
                     </a>{' '}
                     and we're happy to help out!
                  </p>
               </section>
               <div className="flex space-x-4">
                  <Link to={`/liquidity/add/${tokenResourceAddress}?direction=base`} className="btn-primary">
                     Add more liquidity
                  </Link>
                  <Link to={`/token/${tokenResourceAddress}`} className="btn-primary">
                     Go to token page
                  </Link>
               </div>
            </div>
         ) : (
            <>
               <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-12 bg-white rounded-xl lg:rounded-2xl p-4 lg:p-8">
                     <fieldset>
                        <legend className="text-sm font-semibold leading-6 text-gray-900">Token type to launch</legend>
                        <p className="mt-1 text-sm leading-6 text-gray-600">By choosing a token type, we can set some sensible defaults.</p>
                        <div className="mt-6 flex justify-left items-center space-x-6">
                           <div className="flex items-center gap-x-3">
                              <input
                                 id="push-everything"
                                 name="token-type"
                                 type="radio"
                                 value="project"
                                 checked={tokenType == 'project'}
                                 onChange={onTypeChange}
                                 className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                              />
                              <label htmlFor="push-everything" className="block text-sm font-medium leading-6 text-gray-900">
                                 Project Token
                              </label>
                           </div>
                           <div className="flex items-center gap-x-3">
                              <input
                                 id="push-email"
                                 name="token-type"
                                 type="radio"
                                 value="meme"
                                 checked={tokenType == 'meme'}
                                 onChange={onTypeChange}
                                 className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                              />
                              <label htmlFor="push-email" className="block text-sm font-medium leading-6 text-gray-900">
                                 Meme coin
                              </label>
                           </div>
                           <div className="flex items-center gap-x-3">
                              <input
                                 id="push-nothing"
                                 name="token-type"
                                 type="radio"
                                 value="custom"
                                 checked={tokenType == 'custom'}
                                 onChange={onTypeChange}
                                 className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                              />
                              <label htmlFor="push-nothing" className="block text-sm font-medium leading-6 text-gray-900">
                                 Custom
                              </label>
                           </div>
                        </div>
                     </fieldset>

                     <div className="border-b border-gray-900/10 pb-12">
                        <h2 className="text-base font-semibold leading-7 text-gray-900">Token information</h2>
                        <p className="mt-1 text-sm leading-6 text-gray-600">
                           This information will be displayed wherever your token is visible. You will be able to update this data after creating your token, including your token name and symbol.
                        </p>

                        <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-8">
                           <div className="">
                              <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                                 Token name
                              </label>
                              <div className="mt-2">
                                 <div className="flex rounded-md shadow-xs ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                    <input
                                       type="text"
                                       name="name"
                                       id="name"
                                       value={name}
                                       onChange={(evt) => setName(evt.target.value)}
                                       className="block flex-1 border-0 bg-transparent py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                                       placeholder="My Token"
                                    />
                                 </div>
                              </div>
                           </div>
                           <div className="">
                              <label htmlFor="symbol" className="block text-sm font-medium leading-6 text-gray-900">
                                 Symbol
                              </label>
                              <div className="mt-2">
                                 <div className="flex rounded-md shadow-xs ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                    <span className="flex select-none items-center pl-3 text-gray-500 sm:text-sm">$</span>
                                    <input
                                       type="text"
                                       name="symbol"
                                       id="symbol"
                                       value={symbol}
                                       onChange={(evt) => setSymbol(evt.target.value.toUpperCase())}
                                       className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                                       placeholder="TOK"
                                    />
                                 </div>
                              </div>
                           </div>

                           <div className="col-span-full">
                              <label htmlFor="photo" className="block text-sm font-medium leading-6 text-gray-900">
                                 Token icon
                              </label>
                              <div className="mt-2 flex items-center gap-x-3">
                                 {!imagePreview ? <UserCircleIcon title="TIM" className="h-12 w-12 text-gray-300" aria-hidden="true" /> : <img src={imagePreview} className="h-12 w-12 rounded-full" />}
                                 <label
                                    htmlFor="iconUrl"
                                    className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-hidden focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                                 >
                                    <span>Select an image</span>
                                    <input
                                       type="file"
                                       id="iconUrl"
                                       name="iconUrl"
                                       className="sr-only"
                                       accept="image/*"
                                       onChange={(event) => {
                                          onImageChange(event);
                                       }}
                                       onClick={(event) => {
                                          event.target.value = null;
                                       }}
                                    />
                                 </label>
                              </div>
                           </div>

                           <div className="col-span-full">
                              <label htmlFor="about" className="block text-sm font-medium leading-6 text-gray-900">
                                 Description
                              </label>
                              <div className="mt-2">
                                 <textarea
                                    id="about"
                                    name="about"
                                    rows={3}
                                    value={description}
                                    onChange={(evt) => setDescription(evt.target.value)}
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                 />
                              </div>
                              <p className="mt-3 text-sm leading-6 text-gray-600">Explain what your token does in one sentence.</p>
                           </div>

                           <div className="col-span-full">
                              <label htmlFor="url" className="block text-sm font-medium leading-6 text-gray-900">
                                 Website
                              </label>
                              <div className="mt-2">
                                 <div className="flex rounded-md shadow-xs ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                    <input
                                       type="text"
                                       name="url"
                                       id="url"
                                       value={url}
                                       onChange={(evt) => setUrl(evt.target.value)}
                                       className="block flex-1 border-0 bg-transparent py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                                       placeholder="https://"
                                    />
                                 </div>
                              </div>
                              <p className="mt-3 text-sm leading-6 text-gray-600">Where can people find more information about your token?</p>
                           </div>
                        </div>
                     </div>
                     <div className="border-b border-gray-900/10 pb-12">
                        <h2 className="text-base font-semibold leading-7 text-gray-900">Token Tokenomics</h2>
                        <p className="mt-1 text-sm leading-6 text-gray-600">How many tokens will you mint, at what price?</p>

                        <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-8">
                           <div className="">
                              <label htmlFor="supply" className="block text-sm font-medium leading-6 text-gray-900">
                                 Token supply
                              </label>
                              <div className="flex rounded-md shadow-xs ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                 <input
                                    type="text"
                                    name="supply"
                                    id="supply"
                                    value={supply ? supply : ''}
                                    onChange={(evt) => setSupply(+evt.target.value)}
                                    className="block flex-1 border-0 bg-transparent py-1.5 text-gray-900 placeholder:text-gray-400 disabled:text-gray-600 focus:ring-0 sm:text-sm sm:leading-6"
                                    placeholder="100000000"
                                 />
                              </div>
                              <p className="mt-3 text-sm leading-6 text-gray-600">How many tokens do you want to create?</p>
                           </div>
                           <div className="">
                              <label htmlFor="price" className="block text-sm font-medium leading-6 text-gray-900">
                                 Token initial price
                              </label>
                              <div className="flex rounded-md shadow-xs ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                 <input
                                    type="text"
                                    name="price"
                                    id="price"
                                    defaultValue={priceXRD ? priceXRD : ''}
                                    onChange={(evt) => onSetPriceXRD(evt.target.value)}
                                    className="block flex-1 border-0 bg-transparent py-1.5 text-gray-900 placeholder:text-gray-400 disabled:text-gray-600 focus:ring-0 sm:text-sm sm:leading-6"
                                    placeholder="0.001"
                                 />
                                 <span className="flex select-none items-center pr-3 text-gray-500 sm:text-sm">XRD</span>
                              </div>
                              <p className="mt-3 text-sm leading-6 text-gray-600">What is the starting price of your token?</p>
                           </div>
                           <div className="">
                              <label htmlFor="supply" className="block text-sm font-medium leading-6 text-gray-900">
                                 Fully Diluted Market Cap
                              </label>
                              <div className="flex rounded-md shadow-xs ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                 <input
                                    type="text"
                                    name="supply"
                                    id="supply"
                                    disabled={true}
                                    value={supply && priceXRD ? supply * priceXRD : ''}
                                    className="block flex-1 border-0 bg-transparent py-1.5 text-gray-900 placeholder:text-gray-400 disabled:text-gray-600 focus:ring-0 sm:text-sm sm:leading-6"
                                    placeholder="100000000"
                                 />
                                 <span className="flex select-none items-center pr-3 text-gray-500 sm:text-sm">XRD</span>
                              </div>
                              <p className="mt-3 text-sm leading-6 text-gray-600">What are all tokens together worth?</p>
                           </div>
                           <div className="">
                              <label htmlFor="supply" className="block text-sm font-medium leading-6 text-gray-900">
                                 in USD
                              </label>
                              <div className="flex rounded-md shadow-xs ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                 <span className="flex select-none items-center pl-3 text-gray-500 sm:text-sm">US$</span>
                                 <input
                                    type="text"
                                    name="supply"
                                    id="supply"
                                    disabled={true}
                                    value={supply && price ? supply * price : ''}
                                    className="block flex-1 border-0 bg-transparent py-1.5 text-gray-900 placeholder:text-gray-400 disabled:text-gray-600 focus:ring-0 sm:text-sm sm:leading-6"
                                    placeholder="10000"
                                 />
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
                  <div className="space-y-8">
                     <h2 className="text-2xl font-bold text-left px-8 text-white">Preview</h2>

                     {!symbol ? (
                        <section className="space-y-4 bg-white text-black rounded-xl lg:rounded-2xl p-4 lg:p-8">
                           <p>The preview of the token you are creating will appear here.</p>
                        </section>
                     ) : (
                        <>
                           <section className="space-y-4 bg-white text-black rounded-xl lg:rounded-2xl p-4 lg:p-8">
                              <h1 className="text-2xl font-bold text-left flex items-center">
                                 <img className="shrink-0 h-12 w-12 mr-3 rounded-full bg-white" src={imageURL || imagePreview} onError={(evt) => onIconError(evt)} alt="" />
                                 {name}
                                 <span className="text-xl ml-3 text-primary-600">${symbol}</span>
                              </h1>
                              <p>{description}</p>
                              {url ? (
                                 <p>
                                    <a href={url} target="_blank" className="underline text-primary-500">
                                       {url}
                                    </a>
                                 </p>
                              ) : null}
                              {/* <p>
                     {prettyValue(supply || 0)} {symbol}
                  </p> */}
                           </section>

                           <h2 className="text-2xl font-bold text-left px-8 text-white">Liquidity Pool</h2>

                           <AccountSelector onAccount={onAccount}></AccountSelector>

                           <section className="flex flex-col bg-white rounded-xl lg:rounded-2xl p-4 lg:p-8 mt-8">
                              <label htmlFor="tokenInput" className="text-sm text-purple-800">
                                 Trading fee
                              </label>
                              <span className="isolate inline-flex rounded-md text-black">
                                 <button
                                    type="button"
                                    onClick={() => setFee(0.004)}
                                    className={classNames(
                                       fee == 0.004 ? 'ring-2  ring-primary-500' : 'ring-1 ring-gray-300',
                                       'grow relative rounded-l-md bg-white hover:bg-gray-50 px-3 py-2  ring-inset focus:z-10',
                                    )}
                                 >
                                    <span>
                                       <p className="text-2xl text-primary-500">0.4%</p>
                                       <p>Best for project tokens</p>
                                    </span>
                                 </button>

                                 <button
                                    type="button"
                                    onClick={() => setFee(0.01)}
                                    className={classNames(
                                       fee !== 0.004 ? 'ring-2  ring-primary-500' : 'ring-1 ring-gray-300',
                                       'grow relative rounded-r-md bg-white hover:bg-gray-50 px-3 py-2  ring-inset focus:z-10',
                                    )}
                                 >
                                    <span>
                                       <p className="text-2xl text-primary-500">1%</p>
                                       <p>Best for meme coins</p>
                                    </span>
                                 </button>
                              </span>
                           </section>

                           <p className="flex justify-between py-4 px-8">
                              <svg className="w-6 h-6" viewBox="8 8 24 24" xmlns="http://www.w3.org/2000/svg">
                                 <path
                                    d="M29.088 21.972c.432-.443.432-1.212 0-1.654a1.128 1.128 0 0 0-1.617 0l-6.322 6.466V10.58c0-.645-.513-1.17-1.144-1.17-.63 0-1.143.525-1.143 1.17v16.203l-6.321-6.466a1.128 1.128 0 0 0-1.618 0 1.178 1.178 0 0 0-.335.827c0 .312.12.606.336.827l7.964 8.143a1.556 1.556 0 0 0 2.237 0l7.963-8.143z"
                                    fill="currentColor"
                                    fillRule="nonzero"
                                 ></path>
                              </svg>
                           </p>

                           <section className="flex flex-col bg-white rounded-xl lg:rounded-2xl p-4 lg:p-8">
                              <div className="flex justify-between items-center">
                                 <label htmlFor="tokenInput" className="text-sm text-purple-800">
                                    Base Tokens
                                 </label>
                              </div>
                              <div className="flex flex-wrap justify-between items-center">
                                 <TokenInput value={baseTokensRequired} onChange={() => {}} />

                                 <TokenRow
                                    token={{
                                       name,
                                       symbol,
                                       iconUrl: imagePreview,
                                    }}
                                 />
                              </div>
                           </section>

                           <section className="flex flex-col bg-white rounded-xl lg:rounded-2xl p-4 lg:p-8">
                              <div className="flex justify-between items-center">
                                 <label htmlFor="tokenInput" className="text-sm text-purple-800">
                                    Quote Tokens
                                 </label>
                                 {walletFetcher.data ? (
                                    <span className="w-60 text-right lg:text-left text-sm font-normal text-black truncate">
                                       Balance: {(Math.round((walletFetcher.data.filter((b) => b.address == QUOTE_TOKEN_RESOURCE_ADDRESS)[0]?.amount || 0) * 1000000) / 1000000).toLocaleString()}
                                    </span>
                                 ) : null}
                              </div>
                              <div className="flex flex-wrap justify-between items-center">
                                 <TokenInput value={'' + MINIMUM_DFP2_AMOUNT} onChange={() => {}} />

                                 <TokenRow token={quoteToken} />
                              </div>
                           </section>

                           {!tokenResourceAddress ? (
                              <>
                                 <p className="px-8">We first create your token, and with a second transaction create the liquidity pool.</p>

                                 <TransactionButton title={`Create ${symbol} token`} onClick={onCreateToken} />
                              </>
                           ) : (
                              <>
                                 <p className="px-8">Your token was successfully created, now let's create the liquidity pool.</p>
                                 <p className="px-8">
                                    When creating a new pool an equal value of your tokens and DFP2 must be provided, which will be donated to the protocol. This minimum amount of liquidity is used to
                                    prevent the pool from ever running empty, which could lead to price manipulation. After creating the pool, you can add liquidity.
                                 </p>

                                 <TransactionButton title={`Create ${symbol} liquidity pool`} onClick={onCreatePool} />
                              </>
                           )}
                        </>
                     )}
                  </div>
               </div>
            </>
         )}
      </Form>
   );
}

export async function action({ request, context, params }: ActionFunctionArgs) {
   
}
